// Left-panel layer list. Each card shows a live thumbnail of the composite
// up to that layer, plus eye / solo / drag / delete controls. Empty state
// offers quick starts. Add menu includes generators, filters, and combos.

import { effect } from '@/core/signals';
import {
  state,
  selectLayer,
  toggleLayer,
  toggleSolo,
  removeLayer,
  moveLayer,
  addLayer,
} from '@/core/state';
import {
  getLayerSpec,
  listSpecs,
  makeDefaultLayer,
  type LayerSpec,
} from '@/core/pipeline';
import type { Layer } from '@/core/types';
import { el, on, replaceChildren } from '@/lib/dom';
import { ICON } from './icons';
import { surpriseMe } from '@/lib/surprise';
import { openPresetGallery } from './presetGallery';
import { COMBOS, applyCombo, type Combo } from '@/lib/combos';
import {
  subscribeThumbnails,
  requestThumbnails,
  type ThumbnailMap,
} from './layerThumbnails';

export function mountLayerList(host: HTMLElement): () => void {
  const head = el('div', { class: 'll-head' }, [
    el('span', { class: 'll-head-title' }, ['Layers']),
    addButton(),
  ]);
  const list = el('div', { class: 'll-list', role: 'listbox', ariaLabel: 'Layers' });
  host.appendChild(head);
  host.appendChild(list);

  let lastThumbs: ThumbnailMap = new Map();

  const offThumbs = subscribeThumbnails((m) => {
    lastThumbs = m;
    // refresh thumbnail src on existing cards without rebuilding the whole list
    for (const [layerId, url] of m) {
      const img = list.querySelector(
        `[data-id="${cssEscape(layerId)}"] .ll-thumb img`,
      ) as HTMLImageElement | null;
      if (img && img.src !== url) img.src = url;
    }
  });

  const dispose = effect(() => {
    const s = state.get();
    renderList(list, s.layers, s.selectedLayerId, s.soloLayerId, lastThumbs);
    requestThumbnails(s);
  });

  return () => {
    dispose();
    offThumbs();
  };
}

function cssEscape(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function renderList(
  host: HTMLElement,
  layers: Layer[],
  selectedId: string | null,
  soloId: string | null,
  thumbs: ThumbnailMap,
): void {
  if (layers.length === 0) {
    replaceChildren(host, emptyState());
    return;
  }
  const items: HTMLElement[] = [];
  for (let i = layers.length - 1; i >= 0; i--) {
    items.push(
      buildItem(layers[i] as Layer, i, layers.length, selectedId, soloId, thumbs),
    );
  }
  replaceChildren(host, ...items);
}

function emptyState(): HTMLElement {
  const surprise = el('button', { class: 'btn ll-empty-btn primary' }, ['🎲  Surprise me']);
  on(surprise, 'click', () => surpriseMe());
  const presets = el('button', { class: 'btn ll-empty-btn' }, ['Browse presets']);
  on(presets, 'click', () => openPresetGallery());
  const blank = el('button', { class: 'btn ll-empty-btn' }, ['Start blank']);
  on(blank, 'click', () => {
    const s = makeDefaultLayer('solid', '_');
    addLayer({
      kind: s.kind,
      params: { color: '#ff5a1f' },
      blend: s.blend,
      opacity: s.opacity,
      enabled: s.enabled,
      seed: s.seed,
    });
  });
  return el('div', { class: 'll-empty' }, [
    el('div', { class: 'll-empty-icon' }, ['◾◾◾']),
    el('div', { class: 'll-empty-title' }, ['Empty stack']),
    el('div', { class: 'll-empty-hint' }, ['Compose layers from generators and filters.']),
    surprise,
    presets,
    blank,
  ]);
}

function buildItem(
  layer: Layer,
  index: number,
  total: number,
  selectedId: string | null,
  soloId: string | null,
  thumbs: ThumbnailMap,
): HTMLElement {
  const spec = getLayerSpec(layer.kind);
  const isSelected = layer.id === selectedId;
  const isSoloed = layer.id === soloId;
  const dimmed = soloId !== null && !isSoloed;
  const node = el('div', {
    class:
      `ll-item${isSelected ? ' selected' : ''}` +
      `${layer.enabled ? '' : ' off'}${isSoloed ? ' soloed' : ''}${dimmed ? ' dimmed' : ''}`,
    role: 'option',
    tabindex: 0,
    ariaSelected: isSelected ? 'true' : 'false',
    draggable: true,
    dataset: { id: layer.id, index: String(index) },
  });

  const handle = el('span', { class: 'll-handle', innerHTML: ICON.drag });

  const eye = el('button', {
    class: 'btn-icon ll-eye',
    ariaLabel: layer.enabled ? 'Hide layer' : 'Show layer',
    title: layer.enabled ? 'Hide (V)' : 'Show (V)',
    innerHTML: layer.enabled ? ICON.eye : ICON.eyeOff,
  });

  const solo = el('button', {
    class: `btn-icon ll-solo${isSoloed ? ' on' : ''}`,
    ariaLabel: isSoloed ? 'Unsolo layer' : 'Solo layer',
    title: isSoloed ? 'Unsolo' : 'Solo (S)',
  }, ['S']);

  const thumb = el('div', { class: 'll-thumb' });
  const thumbImg = el('img', { ariaLabel: '' }) as HTMLImageElement;
  thumbImg.src = thumbs.get(layer.id) ?? '';
  thumbImg.alt = '';
  thumbImg.draggable = false;
  thumb.appendChild(thumbImg);

  const title = el('div', { class: 'll-title' }, [
    el('span', { class: 'll-kind' }, [spec?.label ?? layer.kind]),
    el(
      'span',
      { class: 'll-meta' },
      [`${spec?.category ?? '·'} · ${Math.round(layer.opacity * 100)}%`],
    ),
  ]);

  const trash = el('button', {
    class: 'btn-icon ll-trash',
    ariaLabel: 'Remove layer',
    innerHTML: ICON.trash,
  });

  node.append(handle, eye, solo, thumb, title, trash);

  on(node, 'click', (ev) => {
    if ((ev.target as HTMLElement).closest('button')) return;
    selectLayer(layer.id);
  });
  on(node, 'keydown', (ev) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      selectLayer(layer.id);
    } else if (ev.key === 'Delete' || ev.key === 'Backspace') {
      ev.preventDefault();
      removeLayer(layer.id);
    } else if (ev.key.toLowerCase() === 'v' && !ev.metaKey && !ev.ctrlKey) {
      ev.preventDefault();
      toggleLayer(layer.id);
    } else if (ev.key.toLowerCase() === 's' && !ev.metaKey && !ev.ctrlKey) {
      ev.preventDefault();
      toggleSolo(layer.id);
    } else if ((ev.metaKey || ev.ctrlKey) && ev.key === 'ArrowUp') {
      ev.preventDefault();
      moveLayer(layer.id, Math.min(total - 1, index + 1));
    } else if ((ev.metaKey || ev.ctrlKey) && ev.key === 'ArrowDown') {
      ev.preventDefault();
      moveLayer(layer.id, Math.max(0, index - 1));
    }
  });
  on(eye, 'click', (ev) => { ev.stopPropagation(); toggleLayer(layer.id); });
  on(solo, 'click', (ev) => { ev.stopPropagation(); toggleSolo(layer.id); });
  on(trash, 'click', (ev) => { ev.stopPropagation(); removeLayer(layer.id); });

  // Drag-and-drop reorder
  on(node, 'dragstart', (ev) => {
    const dt = (ev as DragEvent).dataTransfer;
    if (!dt) return;
    dt.effectAllowed = 'move';
    dt.setData('application/x-pixellab-layer', layer.id);
    node.classList.add('dragging');
  });
  on(node, 'dragend', () => node.classList.remove('dragging'));
  on(node, 'dragover', (ev) => {
    ev.preventDefault();
    node.classList.add('drag-over');
  });
  on(node, 'dragleave', () => node.classList.remove('drag-over'));
  on(node, 'drop', (ev) => {
    ev.preventDefault();
    node.classList.remove('drag-over');
    const dt = (ev as DragEvent).dataTransfer;
    const draggedId = dt?.getData('application/x-pixellab-layer');
    if (!draggedId || draggedId === layer.id) return;
    moveLayer(draggedId, index);
  });

  return node;
}

function addButton(): HTMLElement {
  const btn = el('button', {
    class: 'btn-icon ll-add',
    ariaLabel: 'Add layer',
    title: 'Add layer (A)',
    innerHTML: ICON.plus,
  });
  on(btn, 'click', () => openAddMenu(btn));
  return btn;
}

let openMenu: HTMLElement | null = null;

function openAddMenu(anchor: HTMLElement): void {
  if (openMenu) {
    closeMenu();
    return;
  }
  const menu = el('div', { class: 'menu menu-add', role: 'menu' });
  const r = anchor.getBoundingClientRect();
  Object.assign(menu.style, { top: `${r.bottom + 4}px`, left: `${r.left}px` });

  // Combos first (for first-time users — biggest "wow" per click)
  if (COMBOS.length > 0) {
    menu.appendChild(el('div', { class: 'menu-section' }, ['Combos']));
    for (const c of COMBOS) {
      menu.appendChild(comboItem(c));
    }
  }

  menu.appendChild(el('div', { class: 'menu-section' }, ['Generators']));
  for (const spec of listSpecs('generator')) {
    menu.appendChild(menuItem(spec));
  }
  menu.appendChild(el('div', { class: 'menu-section' }, ['Filters']));
  for (const spec of listSpecs('filter')) {
    menu.appendChild(menuItem(spec));
  }

  document.body.appendChild(menu);
  openMenu = menu;

  const offClick = on(document, 'pointerdown', (ev) => {
    if (!menu.contains(ev.target as Node) && ev.target !== anchor) {
      closeMenu();
      offClick();
    }
  });
}

function menuItem(spec: LayerSpec): HTMLElement {
  const item = el('button', { class: 'menu-item', role: 'menuitem' }, [
    el('span', { class: 'menu-item-label' }, [spec.label ?? spec.kind]),
    el('span', { class: 'menu-item-hint mono' }, [spec.category]),
  ]);
  on(item, 'click', () => {
    const tmp = makeDefaultLayer(spec.kind, '_');
    addLayer({
      kind: tmp.kind,
      params: tmp.params,
      blend: tmp.blend,
      opacity: tmp.opacity,
      enabled: tmp.enabled,
      seed: tmp.seed,
    });
    closeMenu();
  });
  return item;
}

function comboItem(combo: Combo): HTMLElement {
  const item = el('button', { class: 'menu-item menu-item-combo', role: 'menuitem' }, [
    el('span', { class: 'menu-item-label' }, [combo.name]),
    el('span', { class: 'menu-item-hint mono' }, [combo.hint]),
  ]);
  on(item, 'click', () => {
    applyCombo(combo);
    closeMenu();
  });
  return item;
}

function closeMenu(): void {
  if (openMenu) {
    openMenu.remove();
    openMenu = null;
  }
}
